/**
 * 📋 Verification Requirements Audit Log
 * View change history for verification requirements
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  Pagination,
  Divider,
  List,
  ListItem,
  Paper,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Create,
  Delete,
  Edit,
  SwapVert,
} from '@mui/icons-material';
import {
  adminVerificationRequirementsService,
  VerificationRequirementAuditLog,
  PaginationMeta,
} from '@/services/adminVerificationRequirementsService';
import toast from 'react-hot-toast';

interface VerificationRequirementsAuditLogProps {
  onBack?: () => void;
}

export const VerificationRequirementsAuditLogPage: React.FC<VerificationRequirementsAuditLogProps> = ({ onBack }) => {
  const [logs, setLogs] = useState<VerificationRequirementAuditLog[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadAuditLog(currentPage);
  }, [currentPage]);

  const loadAuditLog = async (page: number) => {
    setLoading(true);
    try {
      const response = await adminVerificationRequirementsService.getAuditLog(page, 20);

      if (response.success) {
        setLogs(response.data.logs);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to load audit log');
      }
    } catch (error) {
      console.error('Failed to load audit log:', error);
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Create fontSize="small" />;
      case 'updated':
        return <Edit fontSize="small" />;
      case 'deleted':
        return <Delete fontSize="small" />;
      case 'reordered':
        return <SwapVert fontSize="small" />;
      default:
        return null;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'success';
      case 'updated':
        return 'info';
      case 'deleted':
        return 'error';
      case 'reordered':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    let relative = '';
    if (diffInMinutes < 1) {
      relative = 'just now';
    } else if (diffInMinutes < 60) {
      relative = `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      relative = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      relative = `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else {
      relative = date.toLocaleDateString();
    }

    const absolute = date.toLocaleString();

    return { relative, absolute };
  };

  const formatChanges = (log: VerificationRequirementAuditLog) => {
    if (log.action === 'created') {
      return (
        <Typography variant="body2" color="text.secondary">
          Created new rule: <strong>{log.requirement_name}</strong>
        </Typography>
      );
    }

    if (log.action === 'deleted') {
      return (
        <Typography variant="body2" color="text.secondary">
          Deleted rule: <strong>{log.requirement_name}</strong>
        </Typography>
      );
    }

    if (log.action === 'reordered') {
      return (
        <Typography variant="body2" color="text.secondary">
          Changed priority order
        </Typography>
      );
    }

    // Updated action - show field changes
    if (log.old_values && log.new_values) {
      const changes = Object.keys(log.new_values).map((key) => {
        const oldValue = log.old_values?.[key];
        const newValue = log.new_values?.[key];

        // Format values for display
        const formatValue = (value: any) => {
          if (value === null) return 'null';
          if (value === undefined) return 'undefined';
          if (typeof value === 'boolean') return value ? 'true' : 'false';
          if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'none';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        };

        return (
          <Box key={key} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {key.replace(/_/g, ' ')}:
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip label={formatValue(oldValue)} size="small" variant="outlined" />
              <Typography variant="caption">→</Typography>
              <Chip label={formatValue(newValue)} size="small" color="primary" />
            </Stack>
          </Box>
        );
      });

      return <Box sx={{ mt: 1 }}>{changes}</Box>;
    }

    return null;
  };

  if (loading && logs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          {onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={onBack}
              size="small"
            >
              Back
            </Button>
          )}
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            📋 Verification Requirements - Change History
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Track all changes made to verification requirements
        </Typography>
      </Box>

      {/* Audit Log Entries */}
      {logs.length === 0 ? (
        <Alert severity="info">No audit log entries found.</Alert>
      ) : (
        <>
          <List sx={{ bgcolor: 'background.paper' }}>
            {logs.map((log, index) => {
              const dateInfo = formatDate(log.created_at);

              return (
                <React.Fragment key={log.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      display: 'block',
                      py: 3,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Grid container spacing={2}>
                      {/* Timestamp and Admin */}
                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary" title={dateInfo.absolute}>
                            {dateInfo.relative}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {log.admin_first_name} {log.admin_last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.admin_email}
                          </Typography>
                        </Stack>
                      </Grid>

                      {/* Action and Changes */}
                      <Grid item xs={12} md={9}>
                        <Stack spacing={1}>
                          <Chip
                            icon={getActionIcon(log.action)}
                            label={log.action.toUpperCase()}
                            size="small"
                            color={getActionColor(log.action) as any}
                            sx={{ width: 'fit-content' }}
                          />
                          {formatChanges(log)}
                        </Stack>
                      </Grid>
                    </Grid>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination.total_pages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Pagination Info */}
          {pagination && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              Showing {logs.length} of {pagination.total_count} entries
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default VerificationRequirementsAuditLogPage;
